import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Placeholder for a value the server withheld.
 *
 * This is not a blurred copy of the real number — for signed-out visitors the
 * API never sends `applicationsCount` at all, so there is nothing in the DOM
 * to un-blur, screenshot, or read out of the network tab. What renders here is
 * animated bars standing in for a value the client genuinely does not have.
 *
 * Use it anywhere a locked field would otherwise print. If you find yourself
 * reaching for `blur-sm` on real data instead, redact it server-side first.
 */

interface RedactedValueProps {
  /** How many bars to draw — roughly the width of the value being withheld. */
  bars?: number;
  /** Text after the bars, e.g. "applicants". */
  label?: string;
  /** Where the sign-in prompt points. */
  to?: string;
  /** Prompt text; set to null to render bars only. */
  cta?: string | null;
  className?: string;
}

const RedactedValue: React.FC<RedactedValueProps> = ({
  bars = 2,
  label,
  to = '/login',
  cta = 'Sign in to see',
  className = '',
}) => (
  <span className={`inline-flex items-center gap-1.5 ${className}`} title={cta || undefined}>
    <span className="inline-flex items-center gap-0.5" aria-hidden="true">
      {Array.from({ length: bars }).map((_, i) => (
        <span
          key={i}
          className="signal-redacted-bar"
          // Stagger so the bars pulse in sequence rather than in lockstep.
          style={{ animationDelay: `${i * 160}ms` }}
        />
      ))}
    </span>
    {label && <span className="text-gray-400">{label}</span>}
    {cta && (
      <Link
        to={to}
        className="text-xs font-medium text-primary-600 hover:text-primary-700 hover:underline transition-colors"
      >
        {cta}
      </Link>
    )}
    <span className="sr-only">{cta ? `${label || 'Value'} hidden — ${cta}` : `${label || 'Value'} hidden`}</span>
  </span>
);

export default RedactedValue;
